import {IEndPoint, RespError} from "lib/commons/io/io";
import {CoreUniverse, IBasicUniversePointer, IWsExecFrame, OBasicUniverseConfig} from "lib/core/universe";
import {JNodeInfos, JNodeShortProps, URLTREE, UrlTreeSrv} from "lib/store/urlTree";
import {ISecurityCtxPointer, SEC} from "lib/commons/security";
import {configPackSrv, IPacksInUniverse, OPacksInUniverseConfig, PackSrv} from "lib/wsp/pack";
import {configExecutorSrv, ExecutorSrv, IExecutorInUniverse, OExecutorInUniverseConfig} from "lib/core/executor";
import {IReg, IUiEnv, REG} from "lib/commons/registry";
import {EventsMgr, IEvents, IEventsMgr} from "lib/commons/events";
import {IInfoBrokerPointer} from "lib/commons/infos";
import {configDepotCidSrv, DepotCidSrv, OCidSrvConfig} from "lib/store/cid";
import {DepotLcProv} from "lib/store/collab";
import {DepotResTypeProv, IResType, resPath} from "lib/store/res";
import {ERROR} from "lib/core/errorReport";


export interface ODepotUniverseConfig extends OBasicUniverseConfig, OPacksInUniverseConfig, OExecutorInUniverseConfig {
	cid?: OCidSrvConfig;
	urlTreeAnonymousAccount?: string;
	urlTree?: IEndPoint;
	adminUrlTree?: IEndPoint;
	storeAspects?: EStoreAspects[]
}

export class DepotUniverse extends CoreUniverse implements IPacksInUniverse, IExecutorInUniverse {

	config: ODepotUniverseConfig;

	reg: IReg<IDepotEnv>;

	resTypes: DepotResTypeProv;

	cid: DepotCidSrv;

	urlTree: UrlTreeSrv;

	adminUrlTree: UrlTreeSrv;

	packServer?: PackSrv;

	executor: ExecutorSrv;

	wsFrames: {
		/** Connexion WebSocket généralement utilisé dans les back-office (maj dynamique des res), pas dans les front-office. */
		ws?: IWsExecFrame
	};

	constructor(config: ODepotUniverseConfig) {
		super(config);
	}

	buildConfig(config: ODepotUniverseConfig) {
		if (!config.wsFrames) config.wsFrames = {};
		//if (!config.wsFrames.ws) config.wsFrames.ws = {}; NON : ws optionnel pour les front-office.
		super.buildConfig(config);
		config.cid = configDepotCidSrv(config.httpFrames.web.execFrameUrl, null, config.cid);
		if (config.packs !== null) config.packs = configPackSrv(config.httpFrames.web.execFrameUrl, config.packs);
		if (config.executor !== null) config.executor = configExecutorSrv(config.httpFrames.web.execFrameUrl, config.executor);
		if (!config.urlTree) config.urlTree = config.universeUrl.resolve("tree/");
		if (!config.adminUrlTree) config.adminUrlTree = config.universeUrl.resolve("adminTree/");

	}

	buildUniverse(config: ODepotUniverseConfig) {
		super.buildUniverse(config);
		this.reg.env.resTypes = this.resTypes = new DepotResTypeProv();
		this.cid = new DepotCidSrv(config.cid);
		if (config.packs !== null) this.packServer = new PackSrv(this, config.packs);
		if (config.executor !== null) this.executor = new ExecutorSrv(config.executor);
		this.urlTree = new UrlTreeSrv(config.urlTree, config.urlTreeAnonymousAccount);
		this.adminUrlTree = new UrlTreeSrv(config.adminUrlTree, config.urlTreeAnonymousAccount);
		const ws = this.wsFrames.ws;
		ws?.lcListeners.on('opened', () => {
			if (this.reg.hasPerm("view.depot")) {
				ws.postMsg({
					svc: 'storeChanges',
					listen: true
				});
			}
		});
	}

	/**
	 * Retourne un IReg<IDepotUiEnv> pour une couche graphique.
	 * A utiliser à la création d'une app racine dans la page.
	 * ATTENTION : si noListen!==true, IReg.close() doit être appelé pour libérer les abonnements aux events (si webSocket activé).
	 */
	newDepotUiReg(uiRoot: HTMLElement, noListen?: boolean): IReg<IDepotUiEnv> {
		const dptReg = REG.createSubReg<IDepotUiEnv>(this.reg);
		dptReg.env.uiRoot = uiRoot;
		if (!noListen) this.listenDepotChanges(dptReg);
		return dptReg;
	}

	/**
	 * Retourne un IReg<IDepotUiEnv> qui hérite d'un IReg<IUiEnv> existant.
	 * A utiliser pour la création d'un environnement IDepotUiEnv dans le contexte graphique d'une autre app.
	 * ATTENTION : si noListen!==true, IReg.close() doit être appelé pour libérer les abonnements aux events (si webSocket activé).
	 */
	newDepotUiRegFromUiReg<E extends IUiEnv>(from: IReg<E>, noListen?: boolean): IReg<E & IDepotUiEnv> {
		const dptReg = REG.createSubRegMixed(from, this.reg);
		if (!noListen) this.listenDepotChanges(dptReg);
		return dptReg;
	}

	protected listenDepotChanges(dptReg: IReg<IDepotUiEnv>) {
		const ws = this.reg.env.universe.wsFrames.ws;
		if (ws) {
			const evts = dptReg.env.depotEvents = new EventsMgr<IDepotEvents>();

			function onStorechange(m: JStoreChange) {
				evts.emitCatched("resChange", m)
			}

			function onConnOpened() {
				evts.emitCatched("connOpened")
			}

			ws.msgListeners.on('storeChanges', onStorechange);
			ws.lcListeners.on('opened', onConnOpened);
			dptReg.onRegClose.add(() => {
				ws.lcListeners.removeListener('opened', onConnOpened);
				ws.msgListeners.removeListener('storeChanges', onStorechange);
			});
		}
	}

	/**
	 * Retourne un IReg<IDepotResEnv> pour un path fixé avec mise à disposition du nodeInfos.
	 * ATTENTION : si noListen!==true, IReg.close() doit être appelé pour libérer les abonnements aux events (si webSocket activé).
	 *
	 * Note : à ce stade le registre retourné n'est pas encore enrichi par le IResType.
	 */
	newDepotResReg(path: resPath, noListen?: boolean): IReg<IDepotResEnv> {
		const resReg = REG.createSubReg<IDepotResEnv>(this.reg);
		this.enrichDepotResReg(resReg, path, noListen);
		return resReg;
	}

	/**
	 * Retourne un IReg<IDepotResUiEnv> pour une couche graphique et un path donné.
	 * A utiliser à la création d'une app racine dans la page pointant directement une res.
	 * ATTENTION : si noListen!==true, IReg.close() doit être appelé pour libérer les abonnements aux events (si webSocket activé).
	 *
	 * Note : à ce stade le registre retourné n'est pas encore enrichi par le IResType.
	 */
	newDepotResUiReg(uiRoot: HTMLElement, path: resPath, noListen?: boolean): IReg<IDepotResUiEnv> {
		const resReg = REG.createSubReg<IDepotResUiEnv>(this.reg);
		resReg.env.uiRoot = uiRoot;
		this.enrichDepotResReg(resReg, path, noListen);
		return resReg;
	}

	/**
	 * Retourne un IReg<IDepotResUiEnv> à partir d'un IReg<IDepotUiEnv> et un path donné.
	 * A utiliser à la création d'un env d'une res dans le contexte d'une app IDepotUiEnv.
	 * ATTENTION : si noListen!==true, IReg.close() doit être appelé pour libérer les abonnements aux events (si webSocket activé).
	 *
	 * Note : à ce stade le registre retourné n'est pas encore enrichi par le IResType.
	 */
	newDepotResUiRegFromDepotUiReg(dptReg: IReg<IDepotUiEnv>, path: resPath, noListen?: boolean): IReg<IDepotResUiEnv & IDepotUiEnv> {
		const resReg = REG.createSubReg<IDepotResEnv>(dptReg);
		this.enrichDepotResReg(resReg, path, noListen);
		return resReg;
	}

	/**
	 * Pour switch du processing.
	 */
	forceNewProcessing(reg: IReg<IDepotResUiEnv & IDepotUiEnv>, resType: IResType) {
		reg.resetExtPoints();
		resType.reg.copyTo(reg); //env.resType renseigné par cette copie
	}

	// NON pas fiable delta t entre chargement NodeInfo et écoute des chgts.
	// /**
	//  * Retourne un IReg<IDepotResUiEnv> à partir d'un IReg<IDepotUiEnv> et d'un JNodeInfos déjà chargé.
	//  * ATTENTION : si noListen!==true, IReg.close() doit être appelé pour libérer les abonnements aux events (si webSocket activé).
	//  */
	// newDepotResUiRegFromNodeInfo(dptReg: IReg<IDepotUiEnv>, path: resPath, ni: JNodeInfos, noListen?: boolean): IReg<IDepotResUiEnv & IDepotUiEnv> {
	// 	const resReg = REG.createSubReg<IDepotResEnv>(dptReg);
	// 	resReg.env.path = path;
	// 	resReg.env.nodeInfosPending = null;
	// 	resReg.env.nodeInfos = ni;
	// 	resReg.env.resTypes.getResTypeFor(ni).reg.copyTo(resReg); //env.resType renseigné par cette copie
	// 	resReg.env.nodeInfosError = null;
	// 	if (!noListen) listenWsForRes(resReg);
	// 	return resReg;
	// }

	/**
	 * Retourne un IReg<IDepotResUiEnv> à partir d'un IReg<IDepotUiEnv> et un path+processing donné pour un contexte en création.
	 * ATTENTION : si noListen!=true et path!=null, IReg.close() doit être appelé pour libérer les abonnements aux events (si webSocket activé).
	 *
	 * @param path peut être null pour l'édition des métas d'identification.
	 */
	newDepotResUiRegInCreation(dptReg: IReg<IDepotUiEnv>, path: resPath | null, resType: IResType, noListen?: boolean): IReg<IDepotResUiEnv & IDepotUiEnv> {
		const resReg = REG.createSubReg<IDepotResEnv>(dptReg);
		resReg.env.path = path;
		resReg.env.nodeInfos = {
			path,
			metas: {
				path,
				processing: resType.prc,
			}
		};
		resReg.env.resType = resType;
		if (path != null && !noListen) listenWsForRes(resReg);
		return resReg;
	}


	/**
	 * Retourne un IReg<IDepotUiEnv> qui hérite d'un IReg<IUiEnv> existant qui n'est pas de type IReg<IDepotUiEnv>.
	 * A utiliser pour la création d'un environnement IDepotResUiEnv dans le contexte graphique d'une autre app.
	 * ATTENTION : si noListen!==true, IReg.close() doit être appelé pour libérer les abonnements aux events (si webSocket activé).
	 */
	newDepotResUiRegFromUiReg<E extends IUiEnv>(from: IReg<E>, path: resPath, noListen?: boolean): IReg<E & IDepotResUiEnv & IDepotUiEnv> {
		const resReg = REG.createSubRegMixed(from, this.reg) as IReg<E & IDepotResUiEnv>;
		if (!noListen) this.listenDepotChanges(resReg);
		this.enrichDepotResReg(resReg, path, noListen);
		return resReg;
	}

	/**
	 * Retourne les renderings disponibles sur cette ressource
	 */
	async getRenderings(resReg: IReg<IDepotResEnv | IDepotShortResEnv>): Promise<IResRenderings> {
		if (!resReg.env.renderings) {
			const endPoint = this.urlTree.url.resolve(URLTREE.path2RelativeUrl(resReg.env.path + "/:api:/v1/renderings"));
			resReg.env.renderings = endPoint.fetchJson().then((datas: IResRenderingsDatas | null) => {
				return (resReg.env.renderings = new ResRenderings(datas));
			}).catch((e) => {
				ERROR.log("Fetch renderings failed", e);
				return (resReg.env.renderings = new ResRenderings(null));
			});
		}
		return resReg.env.renderings;
	}

	protected enrichDepotResReg(resReg: IReg<IDepotResEnv>, path: resPath, noListen?: boolean): IReg<IDepotResEnv> {
		resReg.env.path = path;
		fetchNodeInfos(resReg);
		if (!noListen) listenWsForRes(resReg);
		return resReg;
	}

	/**
	 * Renseigne sur les aspects du store gérés dans cet univers
	 * @param aspect
	 */
	hasAspect(aspect: EStoreAspects): boolean {
		return (this.config.storeAspects && this.config.storeAspects.indexOf(aspect) > -1) ? true : false;
	}
}

/** Gestion des renderings */
class ResRenderings implements IResRenderings {

	protected _set = new Set<string>();

	constructor(public datas: IResRenderingsDatas | null) {
		if (datas) for (let r of datas.renderings) {
			for (let subPath of r.subPaths) this._set.add(subPath);
		}
	}

	isAvailable(subPath: string): boolean {
		return this._set.has(subPath)
	}

}

function listenWsForRes(resReg: IReg<IDepotResEnv>) {
	const ws = resReg.env.universe.wsFrames.ws;
	if (ws) {
		resReg.env.nodeInfosChange = new ResEventsMgr(resReg);

		function onStorechange(m: JStoreChange) {
			if (m.props.path === resReg.env.nodeInfos?.permaPath) fetchNodeInfos(resReg)
		}

		function onConnOpened() {
			fetchNodeInfos(resReg)
		}

		ws.msgListeners.on('storeChanges', onStorechange);
		ws.lcListeners.on('opened', onConnOpened);
		resReg.onRegClose.add(() => {
			ws.lcListeners.removeListener('opened', onConnOpened);
			ws.msgListeners.removeListener('storeChanges', onStorechange);
		});
	}
}

async function fetchNodeInfos(resReg: IReg<IDepotResEnv>) {
	//resReg.env.nodeInfos = null; provoquerait des NPE
	const env = resReg.env;
	const promise: Promise<JNodeInfos> = env.nodeInfosPending = env.universe.adminUrlTree.nodeInfos(env.path).then((newNi) => {
		if (env.nodeInfosPending !== promise) return env.nodeInfosPending; //raceCond
		env.nodeInfosPending = null;
		const oldNi = env.nodeInfos;
		if (env.nodeInfos !== undefined) resReg.resetExtPoints();
		env.nodeInfos = newNi;
		env.resTypes.getResTypeFor(newNi).reg.copyTo(resReg); //env.resType renseigné par cette copie
		env.securityCtx = newNi ? SEC.createSub(resReg.parentReg.env.securityCtx, newNi.roles, undefined, newNi) : resReg.parentReg.env.securityCtx;
		env.nodeInfosError = null;
		env.nodeInfosChange?.emit("nodeChange", newNi, oldNi);
		return newNi;
	}, (e) => {
		const oldNi = env.nodeInfos;
		env.nodeInfosError = e;
		env.nodeInfosPending = null;
		env.nodeInfos = undefined;
		env.securityCtx = resReg.parentReg.env.securityCtx;
		env.resType = undefined;
		resReg.resetExtPoints();
		env.nodeInfosChange?.emit("nodeChange", undefined, oldNi);
		throw e;
	});
}

export enum EStoreAspects {
	'trash' = 'trash',
	'unlisted' = 'unlisted',
}

class ResEventsMgr extends EventsMgr<IDepotResChanges> {

	constructor(public reg: IReg<IDepotResEnv>) {
		super()
	}

	on<K extends keyof IDepotResChanges>(event: K, listener: IDepotResChanges[K], order?: number): this {
		if (!this._listeners || !this._listeners[event]) {
			//1er ajout pour cet event.
			if (event === 'urlAncChange') {
				const ws = this.reg.env.universe.wsFrames.ws;
				const onStoreChange = (m: JStoreChange) => {
					if (URLTREE.isDescendantPath(m.props.path, this.reg.env.nodeInfos.permaPath)) {
						const anc = this.reg.env.nodeInfos.urlAnc;
						this.reg.env.universe.adminUrlTree.nodeInfos(this.reg.env.nodeInfos.permaPath, "&props=&persistMetas=&historyProps=&succProps=").then((ni: JNodeInfos | null) => {
							if (this.reg.isClosed) return; //racecond
							this.reg.env.nodeInfos.urlAnc = ni.urlAnc;
							this.emit("urlAncChange", ni, anc);
						}).catch(() => {
							this.emit("urlAncChange", undefined, anc)
						});
					}
				}
				ws.msgListeners.on('storeChanges', onStoreChange);
				this.reg.onRegClose.add(() => {
					ws.msgListeners.removeListener('storeChanges', onStoreChange)
				});
			} else if (event === 'histChange') {
				const ws = this.reg.env.universe.wsFrames.ws;
				const onStoreChange = (m: JStoreChange) => {
					if (URLTREE.extractUnversionedLeafPath(m.props.path) === URLTREE.extractUnversionedLeafPath(this.reg.env.nodeInfos.permaPath)) {
						this.reg.env.universe.adminUrlTree.nodeInfos(this.reg.env.nodeInfos.permaPath, "&props=&persistMetas=&urlAncProps=").then((ni: JNodeInfos | null) => {
							if (this.reg.isClosed) return; //racecond
							const nodeInfos = this.reg.env.nodeInfos;
							nodeInfos.hist = ni.hist;
							nodeInfos.succ = ni.succ;
							this.emit("histChange", nodeInfos);
						}).catch(() => {
							this.emit("histChange", undefined)
						});
					}
				}
				ws.msgListeners.on('storeChanges', onStoreChange);
				this.reg.onRegClose.add(() => {
					ws.msgListeners.removeListener('storeChanges', onStoreChange)
				});
			} else if (event === 'childChange') {
				const ws = this.reg.env.universe.wsFrames.ws;
				const onStoreChange = (m: JStoreChange) => {
					if (URLTREE.extractParentPath(m.props.path) === this.reg.env.nodeInfos.permaPath) this.emit("childChange", m.props);
				}
				ws.msgListeners.on('storeChanges', onStoreChange);
				this.reg.onRegClose.add(() => {
					ws.msgListeners.removeListener('storeChanges', onStoreChange)
				});
			}
		}
		return super.on(event, listener, order);
	}
}

export interface JStoreChange {
	svc: 'storeChanges'
	props: JStoreChangeProps
}

export interface JStoreChangeProps {
	path: resPath
	processing?: string
	movedTo?: resPath
}

interface IDepotUniversePointer extends IBasicUniversePointer {
	universe: DepotUniverse
}

/** Environnement pour un univers depot. */
export type IDepotEnv = IDepotUniversePointer & ISecurityCtxPointer & {
	resTypes: DepotResTypeProv
	lifecycle?: DepotLcProv
}

/**
 * Environnement UI avec notif des chgts de res du depot.
 * Fournit et géré par le DepotUniverse.
 * @see DepotUniverse.newDepotUiReg(uiRoot: Element): IReg<IDepotUiEnv>
 */
export type IDepotUiEnv = IDepotEnv & IUiEnv & IInfoBrokerPointer & {
	/**
	 * Optionnel : null en front-office (pas de refresh dynamique, pas de webSocket).
	 * @param niNew null si suppression
	 * @param niOld null si création
	 */
	depotEvents?: IEventsMgr<IDepotEvents>
}

export function isDepotUiEnv(env: any): env is IDepotUiEnv {
	return env.depotEvents && env.resTypes instanceof DepotResTypeProv
}

/**
 * Events dans le contexte d'un IDepotUiEnv
 */
export interface IDepotEvents extends IEvents {
	resChange: (m: JStoreChange) => void
	connOpened: () => void
}

/**
 * Environnement minimal pour la consultation éphémère d'une ressource.
 */
export type IDepotShortResEnv = IDepotEnv & {
	path: resPath

	/**
	 * NodeInfos chargés.
	 */
	nodeInfos?: JNodeShortProps

	/** IResType associé. */
	resType?: IResType

	/**
	 * Renderings disponibles sur cette res.
	 * @see DepotUniverse.getRenderings();
	 */
	renderings?: IResRenderings | Promise<IResRenderings>
}

export type IDepotShortResUiEnv = IDepotShortResEnv & IUiEnv & IInfoBrokerPointer;


/**
 * Environnement complet avec notif et chgt dynamique du nodeInfos d'une ressource du depot.
 * Fournit et géré par le DepotUniverse.
 * @see DepotUniverse.newDepotResReg(path: string): IReg<IDepotResUiEnv>
 */
export type IDepotResEnv = IDepotEnv & {

	/** Path requété, généralement le livePath. Peut être différent de nodeInfos.permaPath. */
	path: resPath

	/**
	 * NodeInfos chargés.
	 * Si null, cette res n'existe pas.
	 * Si undefined, cf nodeInfosPending ou nodeInfosError.
	 * Si nodeInfosPending est null, alors le chargement est en erreur, cf nodeInfosError.
	 */
	nodeInfos?: JNodeInfos

	/** Une fois le nodeInfos chargé, mémorise son IResType, ou si node en création. */
	resType?: IResType

	/**
	 * NodeInfos en cours de chargement, nullifié une fois chargé.
	 * Retourne null si raceCond
	 */
	nodeInfosPending?: Promise<JNodeInfos | null>

	/** Erreur au chargement du nodeInfos. */
	nodeInfosError?: Error | RespError

	/**
	 * Notifications de changement du node lui même ou des ses voisins (ancêtres, historique)
	 * Optionnel : null en front-office (pas de refresh dynamique, pas de webSocket).
	 */
	nodeInfosChange?: IEventsMgr<IDepotResChanges>

	/**
	 * Renderings disponibles sur cette res.
	 * @see DepotUniverse.getRenderings();
	 */
	renderings?: IResRenderings | Promise<IResRenderings>
}


export type IDepotResChanges = {
	/**
	 * Event sur modification du node lui-même (et pas ses voisins ch, anc, hist, succ...)
	 * @param niNew null si suppression, undefined si fetch en erreur (cf nodeInfosError).
	 * @param niOld null si création
	 */
	nodeChange: (niNew: JNodeInfos, niOld: JNodeInfos) => void

	/**
	 * Event sur modification des ancêtres du node.
	 * @param niNew NodeInfos avec le tableau des ancêtres mis à jour. null si suppression, undefined si fetch en erreur (cf nodeInfosError).
	 * @param ancOld précédent tableau des ancêtres.
	 */
	urlAncChange: (niNew: JNodeInfos, ancOld: JNodeInfos[]) => void

	/**
	 * Event sur modification de l'historique du node (hist + succ).
	 * @param niNew NodeInfos avec les tableaux de l'historique mis à jour. null si suppression, undefined si fetch en erreur (cf nodeInfosError).
	 */
	histChange: (niNew: JNodeInfos) => void

	/**
	 * Event sur modification d'un fils direct du node.
	 */
	childChange: (child: JStoreChangeProps) => void
}

/** Renderings disponibles sur une res. */
export interface IResRenderings {

	/**	Si null, anomalie au fetch, aucun rendering dispo. */
	readonly datas: IResRenderingsDatas | null

	isAvailable(subPath: string): boolean
}

interface IResRenderingsDatas {
	path: resPath,
	urlPublic: string;
	renderings: { subPaths: string[], contentType?: string, tags?: Dict<string[]> }[]
}

/**
 * Environnement UI avec notif et chgt dynamique du nodeInfos d'une ressource du depot.
 * Fournit et géré par le DepotUniverse.
 * @see DepotUniverse.newDepotResUiReg(uiRoot: Element, path: string): IReg<IDepotResUiEnv>
 */
export type IDepotResUiEnv = IDepotResEnv & IUiEnv & IInfoBrokerPointer;



